import type { QueryClient, QueryKey } from '@tanstack/vue-query';
import type { Ref } from 'vue';

const STALE_TIMEOUT_MS = 3000;

// Shared timer map, keyed by a stringified combination of userIndex + mailboxId
const staleProgressorTimers = new Map<string, ReturnType<typeof setTimeout>>();

const getTimerKey = (userIndex: string, mailboxId: string) => `${userIndex}:${mailboxId}`;

export const resetStaleProgressorTimer = (
  queryClient: QueryClient,
  userIndex: Ref<string | null | undefined>,
  mailboxId: string,
  // Called when the progress goes stale without ever being marked complete, so the caller can
  // still reconcile the cache once (e.g. some expected events never arrived).
  onStale?: () => void
) => {
  const currentUserIndex = userIndex.value;
  if (!currentUserIndex) return;

  const key = getTimerKey(currentUserIndex, mailboxId);
  const existing = staleProgressorTimers.get(key);
  if (existing) {
    clearTimeout(existing);
  }

  staleProgressorTimers.set(
    key,
    setTimeout(() => {
      queryClient.setQueryData<null>(
        ['authenticatedQuery', currentUserIndex, 'massAction', 'progress', mailboxId] as QueryKey,
        () => null
      );
      staleProgressorTimers.delete(key);
      onStale?.();
    }, STALE_TIMEOUT_MS)
  );
};

export const clearStaleProgressorTimer = (
  userIndex: Ref<string | null | undefined>,
  mailboxId: string
) => {
  const currentUserIndex = userIndex.value;
  if (!currentUserIndex) return;

  const key = getTimerKey(currentUserIndex, mailboxId);
  const existing = staleProgressorTimers.get(key);
  if (existing) {
    clearTimeout(existing);
    staleProgressorTimers.delete(key);
  }
};
